// AI Turnover Scheduler — Rule-based engine (client v1).
//
// Pure functions: no IO, no side effects. Each rule is documented in
// `docs/business-plan/ai-turnover-scheduler-domain-glossary.md` (sections 3 & 4).
//
// Keep this module decoupled from React, Supabase and date-fns to make it
// trivially portable to the server engine (Epic 5).

import {
  type DayLoadFlags,
  type DayLoadInput,
  type RiskCounts,
  type RiskLabel,
  type RowRiskFlags,
  type SchedulerRowInput,
  TIGHT_WINDOW_HOURS,
  URGENT_WINDOW_HOURS,
} from "./types";

function safeTime(input: string | null | undefined): number | null {
  if (!input) return null;
  const t = new Date(input).getTime();
  return Number.isNaN(t) ? null : t;
}

export function hoursBetween(a: string | null | undefined, b: string | null | undefined): number | null {
  const ta = safeTime(a);
  const tb = safeTime(b);
  if (ta === null || tb === null) return null;
  return (tb - ta) / 36e5;
}

export function hoursUntil(dt: string | null | undefined, now: number = Date.now()): number | null {
  const t = safeTime(dt);
  if (t === null) return null;
  return (t - now) / 36e5;
}

function sameCalendarDay(aIso: string | null | undefined, bIso: string | null | undefined): boolean {
  if (!aIso || !bIso) return false;
  const a = new Date(aIso);
  const b = new Date(bIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Evaluate the four per-row flags for a single platform or external row. */
export function evaluateRowFlags(input: SchedulerRowInput, now: number = Date.now()): RowRiskFlags {
  const span = hoursBetween(input.window_start_at, input.window_end_at);
  const tight_window = span !== null && span <= TIGHT_WINDOW_HOURS;

  const endInH = hoursUntil(input.window_end_at, now);
  const urgent = endInH !== null && endInH <= URGENT_WINDOW_HOURS;

  let unscheduled: boolean;
  if (input.source === "external") {
    if (input.scheduling_precision === "flexible") {
      unscheduled = !input.start_at;
    } else {
      unscheduled = !input.start_at;
    }
  } else {
    unscheduled = !input.scheduled_date || !input.scheduled_time;
  }

  const same_day_turnover = sameCalendarDay(input.checkout_at, input.checkin_at);

  return { tight_window, urgent, unscheduled, same_day_turnover };
}

/** Aggregate per-row flags into batch/portfolio counts. */
export function aggregateRiskCounts(flagsList: RowRiskFlags[]): RiskCounts {
  const counts: RiskCounts = { total: 0, tight: 0, urgent: 0, unscheduled: 0, same_day: 0 };
  for (const f of flagsList) {
    counts.total += 1;
    if (f.tight_window) counts.tight += 1;
    if (f.urgent) counts.urgent += 1;
    if (f.unscheduled) counts.unscheduled += 1;
    if (f.same_day_turnover) counts.same_day += 1;
  }
  return counts;
}

/**
 * Map (counts, optional dayFlags) -> aggregate risk label.
 * Glossary section 3 — "Risk label aggregato".
 */
export function computeRiskLabel(counts: RiskCounts, dayFlags?: DayLoadFlags[]): RiskLabel {
  const overCapacityDays = (dayFlags ?? []).filter((d) => d.over_capacity_day).length;
  const nearCapacityDays = (dayFlags ?? []).filter((d) => d.near_capacity_day).length;
  const sameDayClusters = (dayFlags ?? []).filter((d) => d.same_day_turnover_cluster).length;
  const closedWithLoad = (dayFlags ?? []).filter((d) => d.closed_day_with_load).length;

  const isCritical =
    counts.urgent > 0 ||
    (counts.tight >= 2 && counts.unscheduled > 0) ||
    overCapacityDays > 0 ||
    closedWithLoad > 0;
  if (isCritical) return "critico";

  const isAttention =
    counts.tight > 0 ||
    counts.unscheduled > 0 ||
    nearCapacityDays > 0 ||
    sameDayClusters > 0;
  if (isAttention) return "attenzione";

  return "stabile";
}

/**
 * Compute the four day-level flags. Capacity is the declared sellable
 * capacity for the day (sum of slots, blocked treated as 0).
 */
export function computeDayLoadFlags(input: DayLoadInput): DayLoadFlags {
  const { load, capacity, blocked, sameDayTurnoverRows } = input;

  const effectiveCapacity = blocked ? 0 : Math.max(0, capacity);

  const over_capacity_day = effectiveCapacity > 0 ? load > effectiveCapacity : load > 0 && !blocked;
  const near_capacity_day =
    effectiveCapacity > 0 &&
    !over_capacity_day &&
    (load === effectiveCapacity || effectiveCapacity - load <= 1);
  const closed_day_with_load = (blocked || effectiveCapacity === 0) && load > 0;
  const same_day_turnover_cluster = sameDayTurnoverRows >= 2;

  return { over_capacity_day, near_capacity_day, closed_day_with_load, same_day_turnover_cluster };
}
